// Ziyaret Programı sekmesi — hafta×gün ziyaret yoğunluğu (ısı haritası) +
// gün gün ziyaret sırası düzenleme. Her günün "Sıralama" butonu, o güne atanmış
// müşterilerin ziyaret sırasını düzenleyen ekranı (VisitOrderModal) açar.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { authHeaders } from '../listPageStyles';
import { API_BASE, DAY_OPTIONS, pick, unwrap } from './routePlanUtils';
import VisitOrderModal from './VisitOrderModal';

// #908 isEdit: İzle (salt-okunur) modunda ziyaret sırası düzenlenemez; "Sırala" ile açılan
// modal salt-okunur olur. "Düzenle"ye geçilince (isEdit=true) düzenleme + kaydetme açılır.
export default function IntervalsTab({ planId, isEdit = false, showToast }) {
  const [grid, setGrid] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orderDay, setOrderDay] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [gRes, cRes] = await Promise.all([
        fetch(`${API_BASE}/${planId}/interval-grid`, { headers: authHeaders() }),
        fetch(`${API_BASE}/${planId}/customers`, { headers: authHeaders() }),
      ]);
      if (!gRes.ok) throw new Error(`Grid alınamadı (${gRes.status})`);
      setGrid(unwrap(await gRes.json()));
      setCustomers(cRes.ok ? (unwrap(await cRes.json()) || []) : []);
    } catch (e) {
      showToast?.('error', e.message);
    } finally { setLoading(false); }
  }, [planId, showToast]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const poolCount = grid?.customerPoolCount ?? grid?.CustomerPoolCount ?? 0;
  const weeks = grid?.weeks ?? grid?.Weeks ?? [];

  // Gün → o güne atanmış müşteriler (ziyaret sırasına göre).
  const customersByDay = useMemo(() => {
    const map = {};
    customers.forEach((c) => {
      // #711: Müşteri birden çok güne atalıysa sunucu TEK satırda `dayNos` dizisi döner (tekil
      // `dayNo` null olur). Sadece `dayNo`ya bakılırsa çok-günlü müşteri atlanıp o günlerin
      // "Sırala" düğmesi hiç çıkmıyordu. Her atanmış güne ayrı ayrı ekle.
      const arr = pick(c, 'dayNos', 'DayNos');
      const days = Array.isArray(arr) && arr.length
        ? [...new Set(arr.map(Number))]
        : (() => { const d = pick(c, 'dayNo', 'DayNo'); return d ? [Number(d)] : []; })();
      days.forEach((dn) => { (map[dn] = map[dn] || []).push(c); });
    });
    Object.values(map).forEach((list) => list.sort(
      (a, b) => (pick(a, 'visitOrderNo', 'VisitOrderNo') || 0) - (pick(b, 'visitOrderNo', 'VisitOrderNo') || 0),
    ));
    return map;
  }, [customers]);

  const orderCustomers = orderDay ? (customersByDay[orderDay] || []) : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Isı haritası */}
      <div style={card}>
        <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 800 }}>Hafta × Gün Ziyaret Yoğunluğu</h3>
        <p style={{ fontSize: 12.5, color: 'var(--text-secondary,#6b7280)', margin: '0 0 16px' }}>
          Havuzdaki {poolCount} müşterinin döngü içindeki dağılımı (hücre: müşteri sayısı · havuza oran %).
          Program, Müşteriler sekmesindeki frekans/gün atamasından otomatik üretilir.
        </p>

        {loading ? (
          <div style={{ padding: 24, color: 'var(--text-secondary,#6b7280)' }}>Yükleniyor…</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'separate', borderSpacing: 4, minWidth: 620 }}>
              <thead>
                <tr>
                  <th style={headCell}>Hafta</th>
                  {DAY_OPTIONS.map((d) => (
                    <th key={d.value} style={headCell}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: d.color }} />{d.short}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {weeks.map((w) => {
                  const counts = w.dayCounts ?? w.DayCounts ?? [];
                  const percents = w.dayPercents ?? w.DayPercents ?? [];
                  const weekNo = w.weekNo ?? w.WeekNo;
                  return (
                    <tr key={weekNo}>
                      <td style={{ ...headCell, textAlign: 'left' }}>{weekNo}. Hafta</td>
                      {DAY_OPTIONS.map((d, i) => {
                        const cnt = Number(counts[i] || 0);
                        const pct = Number(percents[i] || 0);
                        const intensity = poolCount ? Math.min(cnt / poolCount, 1) : 0;
                        const canSort = (customersByDay[d.value]?.length || 0) > 0;
                        return (
                          <td key={i} style={{
                            ...dataCell,
                            background: cnt > 0 ? `${d.color}${alpha(0.12 + intensity * 0.5)}` : 'rgba(148,163,184,0.06)',
                            borderColor: cnt > 0 ? `${d.color}55` : 'var(--border-color,#eef2f7)',
                          }}>
                            {cnt > 0 ? (
                              <>
                                <div>
                                  <strong style={{ fontSize: 14 }}>{cnt}</strong>
                                  <span style={{ color: 'var(--text-secondary,#6b7280)', fontSize: 11 }}> %{pct}</span>
                                </div>
                                {canSort && (
                                  <button
                                    style={{ ...sortMini, color: d.color, borderColor: `${d.color}55` }}
                                    onClick={() => setOrderDay(d.value)}
                                    title={isEdit ? `${d.label} ziyaret sırasını düzenle` : `${d.label} ziyaret sırasını görüntüle`}
                                  >{isEdit ? '↕ Sırala' : '↕ Sıra'}</button>
                                )}
                              </>
                            ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p style={{ fontSize: 12, color: 'var(--text-secondary,#9ca3af)', margin: '12px 0 0' }}>
          Bir günün rotasını ziyaret sırasına göre düzenlemek için hücredeki "↕ Sırala"yı kullanın.
        </p>
      </div>

      {orderDay && (
        <VisitOrderModal
          planId={planId}
          day={orderDay}
          customers={orderCustomers}
          onClose={() => setOrderDay(null)}
          onSaved={fetchAll}
          showToast={showToast}
          readOnly={!isEdit}
        />
      )}
    </div>
  );
}

// 0..1 alpha → 2 haneli hex.
function alpha(a) {
  const v = Math.round(Math.max(0, Math.min(1, a)) * 255);
  return v.toString(16).padStart(2, '0');
}

const card = {
  background: 'var(--bg-card,#fff)', border: '1px solid var(--border-color,#e5e7eb)',
  borderRadius: 18, padding: 22, boxShadow: 'var(--shadow-card, 0 12px 28px rgba(15,23,42,0.06))',
};
const headCell = {
  padding: '8px 10px', textAlign: 'center', fontSize: 12, fontWeight: 700,
  color: 'var(--text-secondary,#475569)', whiteSpace: 'nowrap',
};
const dataCell = {
  padding: '8px 6px', textAlign: 'center', fontSize: 12, borderRadius: 8,
  border: '1px solid var(--border-color,#eef2f7)', minWidth: 64, verticalAlign: 'top',
};
const sortMini = {
  marginTop: 5, padding: '2px 6px', fontSize: 10, fontWeight: 700, lineHeight: 1.4,
  border: '1px solid', borderRadius: 6, background: 'rgba(255,255,255,0.75)', cursor: 'pointer',
  whiteSpace: 'nowrap',
};
